import { format, isToday, isValid, parse } from 'date-fns'

import { API_DATE_FORMAT, DISPLAY_DATE_FORMAT, DISPLAY_DATE_INPUT_FORMATS } from './consts'
import {
  getCurrentLocaleCode,
  getDateLocale,
  getSupportedDateLocaleCode,
} from './locale'

const MAX_FULL_MONTH_NAME_LENGTH = 5

/**
 * Форматирует дату для отображения в UI-полях и текстовых метках.
 *
 * Для пустого значения возвращает пустую строку, чтобы функцию было удобно использовать
 * с контролируемыми инпутами.
 */
export function formatDisplayDate(date: Date | undefined, dateFormat = DISPLAY_DATE_FORMAT) {
  if (!date) {
    return ''
  }

  return format(date, dateFormat)
}

/**
 * Форматирует дату для компактного отображения: текущую дату заменяет на «Сегодня»,
 * а длинные названия месяцев сокращает.
 */
export function formatCompactDisplayDate(date: Date, localeCode = getCurrentLocaleCode()) {
  const supportedLocaleCode = getSupportedDateLocaleCode(localeCode)

  if (isToday(date)) {
    const today = new Intl.RelativeTimeFormat(supportedLocaleCode, { numeric: 'auto' }).format(
      0,
      'day',
    )

    return today.charAt(0).toLocaleUpperCase(supportedLocaleCode) + today.slice(1)
  }

  const locale = getDateLocale(supportedLocaleCode)
  const fullMonth = format(date, 'MMMM', { locale })
  const monthFormat = fullMonth.length > MAX_FULL_MONTH_NAME_LENGTH ? 'MMM' : 'MMMM'

  return format(date, `d ${monthFormat} yyyy`, { locale })
}

/**
 * Парсит пользовательскую строку даты по переданным форматам.
 *
 * Возвращает первую валидную дату. Дополнительно проверяет результат обратным форматированием
 * в тот же формат, поэтому невозможные даты вроде `31.02.2026` отклоняются, а не нормализуются
 * средствами Date API.
 */
export function parseDisplayDate(
  value: string,
  dateFormats: readonly string[] = DISPLAY_DATE_INPUT_FORMATS,
) {
  const trimmedValue = value.trim()

  if (!trimmedValue) {
    return undefined
  }

  const matchedDateFormat = dateFormats.find((dateFormat) => {
    const date = parse(trimmedValue, dateFormat, new Date())

    return isValid(date) && format(date, dateFormat) === trimmedValue
  })

  if (matchedDateFormat) {
    return parse(trimmedValue, matchedDateFormat, new Date())
  }

  return undefined
}

/**
 * Парсит дату из API-формата в локальной таймзоне пользователя.
 */
export function parseApiDate(value: string) {
  return parseDisplayDate(value, [API_DATE_FORMAT])
}

/**
 * Форматирует дату для отправки в API.
 */
export function formatApiDate(date: Date, dateFormat = API_DATE_FORMAT) {
  return format(date, dateFormat)
}

/**
 * Проверяет одинаковые даты.
 */
export const isSameLocalDate = (dateA: Date, dateB: Date) =>
  dateA.getFullYear() === dateB.getFullYear() &&
  dateA.getMonth() === dateB.getMonth() &&
  dateA.getDate() === dateB.getDate()

/**
 * Возвращает начало календарного дня для переданной даты в локальной таймзоне пользователя.
 */
export const getLocalDateStart = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate())

export const DateHelpers = {
  formatApiDate,
  formatCompactDisplayDate,
  formatDisplayDate,
  getLocalDateStart,
  isSameLocalDate,
  parseApiDate,
  parseDisplayDate,
} as const
